'use client';

import { useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';

import ModalWrapper from '../ModalWrapper/ModalWrapper';
import ds from '@/styles/ds/ds.module.scss';
import { icons } from '@/Icons';
import { useModalStoreActions } from '@/store';
import useUserVerificationLevels from '@/hooks/useUserVerificationLevels/useUserVerificationLevels';

import VerificationModalSteps from '../VerificationModalSteps/VerificationModalSteps';
import { useOnboardingStoreActions } from '@/features/Onboarding/store';

const WalletModalContent = dynamic(
	() => import('./WalletModalContent/WalletModalContent'),
	{ ssr: false }
);

const ClientTabs = dynamic(() => import('./ClientTabs/ClientTabs'), {
	ssr: false,
});

export enum WalletTabEnum {
	Deposit = 'deposit',
	Withdraw = 'withdraw',
	BuyCrypto = 'buy-crypto',
	Balances = 'balances',
}

function WalletModal() {
	const { setModalStatus } = useModalStoreActions();
	const { setShowDepositPopup } = useOnboardingStoreActions();
	const { showVerificationStepsModal } = useUserVerificationLevels();

	const searchParams = useSearchParams();
	const router = useRouter();

	const walletParam = searchParams.get('wallet');
	const tabParam = searchParams.get('tab') as WalletTabEnum;
	const walletTab = tabParam || WalletTabEnum.Deposit;

	const handleClose = () => {
		const url = new URL(window.location.href);
		url.searchParams.delete('wallet');
		url.searchParams.delete('tab');
		router.push(url.pathname + url.search);
		setModalStatus(null);
	};

	const setWalletTab = (tab: WalletTabEnum) => {
		const url = new URL(window.location.href);
		url.searchParams.set('wallet', 'open');
		url.searchParams.set('tab', tab);
		router.push(url.pathname + url.search);
	};

	useEffect(() => {
		setShowDepositPopup(false);
	}, []);

	if (showVerificationStepsModal) {
		return <VerificationModalSteps />;
	}

	return (
		<ModalWrapper onClose={handleClose}>
			<div
				className="bg-midnight-blue w-screen max-w-[600px] h-[100svh] top-0 m-auto md:h-fit max-h-[800px] px-4 py-8 md:px-9 md:py-8 md:m-auto flex rounded-[10px] flex-col gap-5 popup overflow-hidden"
				style={{ boxShadow: '0px 5px 30px 0px rgba(0, 0, 0, 0.60)' }}
			>
				<section className="flex justify-between items-center">
					<p className={`${ds.sectionHeader} flex gap-3 items-center`}>
						<span className="w-6 h-6">
							<icons.wallet />
						</span>
						Wallet
					</p>
					<button
						onClick={handleClose}
						className="top-2 right-3 text-xl font-bold z-10"
						aria-label="Close wallet modal"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="18"
							height="18"
							viewBox="0 0 18 18"
							fill="none"
						>
							<path
								d="M16.4566 3.19978C16.9147 2.74167 16.9147 2.00091 16.4566 1.54767C15.9985 1.09444 15.2577 1.08957 14.8045 1.54767L9.00507 7.34711L3.20076 1.5428C2.74265 1.08469 2.00188 1.08469 1.54865 1.5428C1.09542 2.00091 1.09054 2.74167 1.54865 3.19491L7.34809 8.99434L1.54378 14.7987C1.08567 15.2568 1.08567 15.9975 1.54378 16.4508C2.00188 16.904 2.74265 16.9089 3.19588 16.4508L8.99532 10.6513L14.7996 16.4556C15.2577 16.9137 15.9985 16.9137 16.4517 16.4556C16.905 15.9975 16.9098 15.2568 16.4517 14.8035L10.6523 9.00409L16.4566 3.19978Z"
								fill="#F2F3F5"
								opacity={0.3}
							/>
						</svg>
					</button>
				</section>
				<ClientTabs currentTab={walletTab} setWalletTab={setWalletTab} />
				<WalletModalContent walletTab={walletTab} />
			</div>
		</ModalWrapper>
	);
}

export default WalletModal;
